import { logger } from '@gik/analytics/utils/logger';
import type { RasterAttachImageResult } from '@gik/core/utils/FileUtils';
import FileUtils from '@gik/core/utils/FileUtils';

export type CoverPhotoEditorDirection = 'vertical' | 'horizontal' | 'unset';
export interface ICoverPhotoEditor {
  destroy: Function;
  renderOutputImage(): string;
  direction: CoverPhotoEditorDirection;
}

export async function attachCoverPhotoImage(): Promise<ICoverPhotoEditor> {
  const outputImageWidth = 1440 * 2;
  const outputImageHeight = 494 * 2;
  const attachResult = (await FileUtils.attachImage(outputImageWidth)) as RasterAttachImageResult;

  return new Promise<ICoverPhotoEditor>(resolve => {
    if (attachResult.base64) {
      const image = document.getElementById('inkind-cover-photo-editor-image') as HTMLImageElement;
      if (!image) {
        throw new Error('image not found');
      }
      image.src = attachResult.base64;
      image.draggable = false;
      image.onload = () => {
        resolve(createCoverPhotoEditor(image, outputImageWidth, outputImageHeight));
      };
    } else {
      resolve(null);
    }
  });
}

function createCoverPhotoEditor(
  image: HTMLImageElement,
  outputImageWidth: number,
  outputImageHeight: number
): ICoverPhotoEditor {
  if (!image) return void logger.error('No image object!');

  const editor = document.getElementById('inkind-cover-photo-editor');
  // these are the dimensions and aspect ratio we want to upload

  const fullAspect = outputImageWidth / outputImageHeight;

  // these are the dimensions and aspect ratio which is being displayed to the user
  const editorWidth = editor?.offsetWidth;
  const editorHeight = editor?.offsetHeight;
  //const editorAspect = editorWidth / editorHeight;

  // choose virtual editor based on cover photo size
  const windowAspect = editorWidth / editorHeight;

  // This encompasses the full size of the image that will be uploaded.
  // The virtual editor cannot exist outside the scaled image.
  // On desktop, it'll have extra height than what is being displayed in the editor.
  // On mobile, it'll have extra width than what is being displayed in the editor.
  let virtualEditorWidth = windowAspect >= 1 ? editorWidth : editorHeight * fullAspect;
  let virtualEditorHeight = windowAspect >= 1 ? editorWidth / fullAspect : editorHeight;

  if (virtualEditorHeight < editorHeight) {
    virtualEditorHeight *= editorHeight / virtualEditorHeight;
    virtualEditorWidth *= editorHeight / virtualEditorHeight;
  }
  if (virtualEditorWidth < editorWidth) {
    virtualEditorHeight *= editorWidth / virtualEditorWidth;
    virtualEditorWidth *= editorWidth / virtualEditorWidth;
  }

  //$("#cover-upload-valid-image-panel").removeClass("hidden");
  const dragPanel = document.getElementById('inkind-cover-content');

  image.style.visibility = 'visible';
  const scale = Math.max(
    virtualEditorWidth / image.width,
    virtualEditorHeight / image.height,
    editorWidth / image.width,
    editorHeight / image.height
  );

  let translateX = (editorWidth - image.width * scale) * 0.5;
  let translateY = (editorHeight - image.height * scale) * 0.5;

  const direction =
    Math.floor(image.width * scale) > virtualEditorWidth
      ? 'horizontal'
      : Math.floor(image.height * scale) > virtualEditorHeight
      ? 'vertical'
      : 'unset';

  const dragCursor = direction == 'horizontal' ? 'e-resize' : direction == 'vertical' ? 'n-resize' : 'unset';

  if (dragCursor !== 'n-resize') {
    //$('#cover-upload-centre-button-panel #drag #drag-v').addClass('hidden');
  }
  if (dragCursor !== 'e-resize') {
    //$('#cover-upload-centre-button-panel #drag #drag-h').addClass('hidden');
  }

  if (dragCursor === 'unset') {
    //$('#cover-upload-centre-button-panel #drag').addClass('hidden');
  }

  dragPanel.style.cursor = dragCursor;

  const updateTransform = function () {
    image.style.transform = 'translate(' + translateX + 'px, ' + translateY + 'px) scale(' + scale + ')';
  };
  updateTransform();

  const limitTranslation = function () {
    // LIMIT TRANSLATION to ensure virtual editor stays within image
    translateX = Math.min((editorWidth - virtualEditorWidth) * 0.5, translateX);
    translateX = Math.max(
      -image.width * scale + virtualEditorWidth + (editorWidth - virtualEditorWidth) * 0.5,
      translateX
    );

    translateY = Math.min((editorHeight - virtualEditorHeight) * 0.5, translateY);
    translateY = Math.max(
      -image.height * scale + virtualEditorHeight + (editorHeight - virtualEditorHeight) * 0.5,
      translateY
    );
  };

  let dragX = 0;
  let dragY = 0;
  let isPlatformTouchDown = false;

  function platformTouchEnd() {
    isPlatformTouchDown = false;
    //$('#cover-upload-centre-button-panel').removeClass('hidden');
    dragPanel.style.cursor = dragCursor;
  }

  function platformTouchStart(x, y) {
    isPlatformTouchDown = true;
    dragX = x;
    dragY = y;
    //$('#cover-upload-centre-button-panel').addClass('hidden');
    dragPanel.style.cursor = 'grab';
  }

  function platformTouchMove(x, y) {
    if (isPlatformTouchDown) {
      translateX += x - dragX;
      translateY += y - dragY;
      dragX = x;
      dragY = y;
      limitTranslation();
      updateTransform();
    }
  }

  const mouseDown = function (event) {
    platformTouchStart(event.clientX, event.clientY);
    event.preventDefault();
    event.stopPropagation();
  };

  const mouseUp = function (event) {
    platformTouchEnd();
    event.preventDefault();
    event.stopPropagation();
    // TODO: remove
    renderOutputImage();
  };

  const mouseMove = function (event) {
    platformTouchMove(event.clientX, event.clientY);
    event.preventDefault();
    event.stopPropagation();
  };

  const touchStart = function (event) {
    if (event.touches.length > 0) {
      platformTouchStart(event.touches[0].clientX, event.touches[0].clientY);
    }
  };

  const touchEnd = function () {
    platformTouchEnd();
  };

  const touchMove = function (event) {
    if (event.touches.length > 0) {
      platformTouchMove(event.touches[0].clientX, event.touches[0].clientY);
      event.preventDefault();
      event.stopImmediatePropagation();
    }
  };

  dragPanel.addEventListener('mousedown', mouseDown);
  document.addEventListener('mouseup', mouseUp);
  document.addEventListener('mousemove', mouseMove);
  dragPanel.addEventListener('touchstart', touchStart, { passive: false });
  document.addEventListener('touchend', touchEnd, { passive: false });
  document.addEventListener('touchmove', touchMove, { passive: false });

  const removeEditorListeners = function () {
    dragPanel.removeEventListener('mousedown', mouseDown);
    document.removeEventListener('mouseup', mouseUp);
    document.removeEventListener('mousemove', mouseMove);
    dragPanel.removeEventListener('touchstart', touchStart);
    document.removeEventListener('touchend', touchEnd);
    document.removeEventListener('touchmove', touchMove);
  };

  const renderOutputImage = (): string => {
    const image = document.getElementById('inkind-cover-photo-editor-image') as HTMLImageElement;
    if (!image) {
      throw new Error('image not found');
    }

    const canvas = document.createElement('CANVAS') as HTMLCanvasElement;
    canvas.width = outputImageWidth;
    canvas.height = outputImageHeight;

    // Render image to outputImageWidth, outputImageHeight
    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, canvas.width, canvas.height);

    const sx = -Math.floor(translateX / scale) - Math.floor(((virtualEditorWidth - editorWidth) / scale) * 0.5);
    const sy = -Math.floor(translateY / scale) - Math.floor(((virtualEditorHeight - editorHeight) / scale) * 0.5);
    const sw = Math.floor(virtualEditorWidth / scale);
    const sh = Math.floor(virtualEditorHeight / scale);

    ctx.drawImage(
      image,
      sx * (image.naturalWidth / image.width),
      sy * (image.naturalHeight / image.height),
      sw * (image.naturalWidth / image.width),
      sh * (image.naturalHeight / image.height),
      0,
      0,
      outputImageWidth,
      outputImageHeight
    );
    return canvas.toDataURL('image/jpeg');
  };

  const destroy = () => {
    removeEditorListeners();
    dragPanel.style.cursor = 'unset';
  };

  return {
    destroy,
    renderOutputImage,
    direction,
  };
}
